import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://henhenai.com/article/${article.slug}`,
    inLanguage: 'zh-CN',
    articleSection: article.categoryName,
    keywords: article.tags.join(', '),
    isPartOf: { '@type': 'WebSite', name: '狠狠爱', url: 'https://henhenai.com/' },
    publisher: {
      '@type': 'Organization',
      name: '狠狠爱',
      url: 'https://henhenai.com/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://henhenai.com/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://henhenai.com/category/${article.category}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://henhenai.com/article/${article.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://henhenai.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
      />
      <SchemaOrg data={[articleSchema, breadcrumbSchema]} />
      <Navbar />
      <main className="min-h-screen pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6" itemScope itemType="https://schema.org/Article">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="bg-accent-100 text-accent-800 px-3 py-1 rounded-full hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              <span>{article.date}</span>
              <span>{article.readTime}</span>
              <span>作者：{article.author}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          </header>

          <div className="relative overflow-hidden rounded-lg mb-10 aspect-[2/1]">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="bg-secondary-100 text-secondary-800 text-xs px-3 py-1.5 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-16 mb-12">
              <h3 className="font-heading text-xl text-foreground-950 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative overflow-hidden aspect-[16/10]">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-2">{related.date} · {related.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}